import { useMemo } from "react";
import PageHeader from "@/components/PageHeader";
import TabNavigation from "@/components/tabs/TabNavigation";
import Field from "@/components/fields/Field";
import { Artwork } from "@/components/ArtworkSlot";
import buildTrackTabs from "../trackTabs";

interface Credit {
  name: string;
  role_label: string;
  nro_affiliation: string | null;
  nro_number: string | null;
}

interface CreditsShowProps {
  track: {
    id: number;
    title: string;
    artwork_attached: boolean;
  };
  artwork: Artwork;
  credits: Credit[];
}

const Show: React.FC<CreditsShowProps> = ({ track, artwork, credits }) => {
  const grouped = useMemo(() => {
    const map: Record<string, Credit[]> = {};
    credits.forEach((credit) => {
      if (!map[credit.role_label]) map[credit.role_label] = [];
      map[credit.role_label].push(credit);
    });
    return Object.entries(map);
  }, [credits]);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      <PageHeader
        backLink={{ href: "/tracks", text: "Back to all tracks" }}
        title={track.title}
        primaryAction={{
          href: `/tracks/${track.id}/credits/edit`,
          text: "Edit",
        }}
        artwork={{
          trackId: track.id,
          uploaded: track.artwork_attached,
          thumbnailUrl: artwork.thumbnail_url,
          originalUrl: artwork.original_url,
          alt: `${track.title} artwork`,
        }}
      />
      <TabNavigation
        tabs={buildTrackTabs(track.id, "credits")}
      />
      {credits.length === 0 ? (
        <div className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10">
          <p className="text-cream-600 dark:text-cream-400 text-sm">
            No credits have been added yet.
          </p>
        </div>
      ) : (
        grouped.map(([roleLabel, roleCredits]) => (
          <div
            key={roleLabel}
            className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10"
          >
            <h2 className="text-cream-900 dark:text-cream-100 text-lg font-semibold">
              {roleLabel}
            </h2>
            {roleCredits.map((credit) => (
              <div
                key={credit.name}
                className="border-cream-200 dark:border-cream-800 flex flex-col gap-4 border-b pb-6 last:border-b-0 last:pb-0"
              >
                <Field label="Name" value={credit.name} />
                <Field
                  label="NRO Affiliation"
                  value={credit.nro_affiliation}
                />
                <Field label="NRO Number" value={credit.nro_number} />
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};

export default Show;
